import { normalisePhone } from "@sirdab/returns-shared";

/**
 * What the lookup field does to whatever the shopper actually typed.
 *
 * The comparison rule is NOT redefined here. `normalisePhone()` in
 * `@sirdab/returns-shared` is the one definition of "these two spellings name the same
 * subscriber", and everything below routes through it. This module only adds the two
 * things a browser text field needs on top of it:
 *
 *   1. Arabic-Indic digits. An Arabic keyboard produces `٠٥٠١١١٢٢٣٣`, and `normalisePhone`
 *      strips those as non-digits — the number would silently become empty. RTL shoppers
 *      are the DEFAULT audience of this portal, so folding them to Latin is not a nicety.
 *   2. A plausibility check for the field's own error text. It gates nothing: the server
 *      re-validates, and an implausible number is still sent if the shopper insists, so a
 *      real Saudi format we failed to anticipate cannot lock anyone out of their return.
 */

/** Arabic-Indic (٠-٩) and Extended Arabic-Indic (۰-۹), in code-point order. */
const ARABIC_DIGIT_BLOCKS = [0x0660, 0x06f0] as const;

/** `٠٥٠…` -> `050…`. Anything that is not an Arabic-Indic digit passes through untouched. */
export function toLatinDigits(value: string): string {
  return [...value]
    .map((char) => {
      const code = char.codePointAt(0);
      if (code === undefined) return char;
      for (const base of ARABIC_DIGIT_BLOCKS) {
        if (code >= base && code <= base + 9) return String(code - base);
      }
      return char;
    })
    .join("");
}

/** The nine significant digits, whatever spelling and whatever digit shapes came in. */
export function phoneKey(value: string): string {
  return normalisePhone(toLatinDigits(value));
}

/**
 * Does this look like a Saudi mobile? Nine significant digits starting with 5 — the shape
 * shared by `05xxxxxxxx`, `+9665xxxxxxxx`, `9665xxxxxxxx` and every spacing of those.
 *
 * Advisory only. See the module note: this decides what the field SAYS, never what it sends.
 */
export function isPlausibleSaudiMobile(value: string): boolean {
  const key = phoneKey(value);
  return key.length === 9 && key.startsWith("5");
}

/**
 * The spelling we put on the wire. E.164 when we recognise the number, otherwise the
 * shopper's own text, trimmed.
 *
 * Sending the raw text when we do not recognise it is deliberate: the server compares with
 * the same `normalisePhone`, so a format we mis-read is still matched correctly there, and
 * a number we cannot parse must produce the API's ordinary "not found" — never a
 * client-side refusal to look.
 */
export function toWirePhone(value: string): string {
  const trimmed = toLatinDigits(value).trim();
  return isPlausibleSaudiMobile(trimmed) ? `+966${phoneKey(trimmed)}` : trimmed;
}
